import { useState, useEffect } from 'react';
import { qcAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertTriangle, XCircle, ClipboardCheck, ChevronDown, ChevronUp } from 'lucide-react';

const QC_TYPE_LABELS = { fabric_receipt: 'Fabric Receipt', pre_trial: 'Pre-Trial', post_alteration: 'Post-Alteration' };
const RESULT_CFG = {
  pass: { label: 'Pass', bg: 'rgba(5,150,105,0.08)', text: '#059669', icon: CheckCircle2 },
  flag: { label: 'Flag', bg: 'rgba(217,119,6,0.08)', text: '#D97706', icon: AlertTriangle },
  fail: { label: 'Fail', bg: 'rgba(220,53,69,0.08)', text: '#DC2626', icon: XCircle },
};

export default function QCHistory({ orderId }) {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    qcAPI.listInspections({ order_id: orderId }).then(res => {
      setInspections(res.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return null;
  if (inspections.length === 0) return null;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="qc-history-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
          <ClipboardCheck className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
          QC Inspections
          <Badge variant="outline" className="text-[10px] ml-auto">{inspections.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {inspections.map(insp => {
          const rc = RESULT_CFG[insp.overall_result] || RESULT_CFG.pass;
          const Icon = rc.icon;
          const isExpanded = expanded === insp.id;
          return (
            <div key={insp.id} className="rounded-lg border overflow-hidden"
              style={{ borderColor: rc.text + '25' }}
              data-testid={`qc-inspection-${insp.id}`}>
              <button onClick={() => setExpanded(isExpanded ? null : insp.id)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left"
                style={{ transition: 'background 120ms' }}>
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: rc.bg }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: rc.text }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                      {QC_TYPE_LABELS[insp.type] || insp.type}
                      {insp.garment_type && <span className="text-[10px] font-normal capitalize ml-1.5" style={{ color: 'var(--ink-muted)' }}>({insp.garment_type})</span>}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                      {insp.inspected_by_name} &middot; {insp.inspection_date?.slice(0, 10)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="text-[10px] px-2 py-0.5 font-bold" style={{ backgroundColor: rc.text, color: '#fff' }}>
                    {rc.label}
                  </Badge>
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />}
                </div>
              </button>
              {isExpanded && (
                <div className="px-3 pb-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                  <div className="space-y-1 mt-2">
                    {(insp.checklist || []).map((item, i) => {
                      const ic = RESULT_CFG[item.result] || RESULT_CFG.pass;
                      const ItemIcon = ic.icon;
                      return (
                        <div key={i} className="flex items-center gap-2 py-1">
                          <ItemIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ic.text }} />
                          <span className="text-xs flex-1" style={{ color: 'var(--navy-ink)' }}>{item.check_name}</span>
                          <Badge variant="outline" className="text-[9px] px-1 py-0"
                            style={{ color: ic.text, borderColor: ic.text + '30' }}>{ic.label}</Badge>
                          {item.notes && <span className="text-[9px] italic max-w-[150px] truncate" style={{ color: 'var(--ink-muted)' }}>{item.notes}</span>}
                        </div>
                      );
                    })}
                  </div>
                  {insp.notes && (
                    <p className="text-[10px] mt-2 italic px-1 py-1 rounded" style={{ backgroundColor: 'rgba(27,42,74,0.03)', color: 'var(--ink-muted)' }}>
                      Notes: {insp.notes}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
